import React from 'react';
import { fmtMoney } from '../lib/format';
import { useDisplayOptions } from '../lib/displayOptions';

// ═══════════════════════════════════════════════════════════════════════════
//  THE DOCUMENT, AS A SHEET OF PAPER
//
//  Everything up to this point is a form: fields, steppers, a swipe to delete.
//  This is what the form produced — letterhead, who it is for, the lines with
//  their rate and quantity, and the total. Julian asked to land here every
//  time an estimate or an invoice is saved, and the reason is the one that
//  matters: the form is where you type, and the sheet is where you catch the
//  line you priced at $0.00.
//
//  ── ONE RENDERER, ON SCREEN AND ON PAPER ─────────────────────────────────
//  It draws `.print-doc`, the same class PrintDoc uses, so the print rules in
//  index.css (`@media print` — hide the app, show the sheet) already apply.
//  Print / Save PDF is the browser's own dialog. There is no second template
//  that can drift from what is on the screen.
//
//  ── WHOSE NAME IS AT THE TOP ─────────────────────────────────────────────
//  `from` and `to` are passed in, never assumed. A technician's estimate is
//  THEIR business quoting QPS, so their name is the letterhead and QPS is who
//  it is prepared for. QPS's own invoice to a homeowner is the other way
//  round. Getting this backwards would put a homeowner's name and a customer
//  price in front of a technician, which is the one thing the platform must
//  never do.
// ═══════════════════════════════════════════════════════════════════════════

const NOTE = /^\s*(note|nota)\s*:\s*/i;

// "joist.com/est/1168" in an href is a RELATIVE path: the browser reads it as
// a page on qpshub.com. The server stores links with their scheme, but a sheet
// drawn straight off the form a moment before the save has whatever was typed,
// so the scheme is put back here as well. Anything that is not http(s) gets no
// link at all rather than a broken one.
const href = (v) => {
  const raw = String(v || '').trim();
  if (!raw) return '';
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    return ['http:', 'https:'].includes(u.protocol) ? u.href : '';
  } catch { return ''; }
};

// 08/21/2026 — a document date, not a sentence. The long form ("September 23,
// 2026") reads well in the app and wastes a third of the line on paper.
const docDate = (d) => {
  const date = d ? new Date(d) : new Date();
  if (isNaN(date)) return '';
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

// A line's description is plain text and every newline in it is a bullet —
// exactly as the estimate sheet draws it, so the paper cannot say something
// the screen doesn't. "Note:" sets in italic underneath.
function Scope({ description }) {
  const rows = String(description || '').split('\n').map(r => r.trim()).filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <div className="pd-scope">
      {rows.map((r, i) => (
        NOTE.test(r)
          ? <p key={i} className="pd-scope-note">{r.replace(NOTE, '')}</p>
          : <p key={i}>{r}</p>
      ))}
    </div>
  );
}

// ── What happened after it was sent ───────────────────────────────────────
//
// On the technician's own copy, never on the customer's. The single most
// useful thing to know about a quote you have heard nothing about is whether
// it was ever opened — "sent and never opened" is a wrong phone number, and
// "opened four times and no answer" is a phone call worth making.
const ago = (d) => {
  const then = d ? new Date(d) : null;
  if (!then || isNaN(then)) return '';
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function ShareBand({ share }) {
  if (!share?.sentAt) return null;
  const views = Number(share.viewCount) || 0;
  const decided = share.decision;
  const bits = [
    <span key="s">Sent <b>{ago(share.lastSentAt || share.sentAt)}</b></span>,
    <span key="v">
      {views === 0
        ? <b>not opened yet</b>
        : <>opened <b>{views === 1 ? 'once' : `${views} times`}</b></>}
    </span>,
  ];
  if (decided) {
    bits.push(
      <span key="d">
        {decided === 'accepted' ? 'approved by ' : 'declined by '}
        <b>{share.decidedByName || 'your customer'}</b>
      </span>
    );
  }
  return (
    <div className={`pd-share print-hide ${decided === 'accepted' ? 'yes' : ''}`}>
      {bits.map((b, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="dot">·</span>}
          {b}
        </React.Fragment>
      ))}
    </div>
  );
}

const Party = ({ label, party }) => {
  if (!party) return null;
  const lines = (party.lines || []).filter(Boolean);
  return (
    <div className="pd-to">
      <div className="pd-to-l">{label}</div>
      <div className="pd-to-n">{party.name}</div>
      {lines.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
};

export default function PaperDoc({
  kind = 'estimate',       // 'estimate' | 'invoice'
  number,
  date,
  status,                  // 'DRAFT' | 'PENDING' | 'APPROVED' …
  from = {},               // { name, lines: [] } — the letterhead
  to,                      // { name, lines: [] } — Prepared For
  serviceAddress,          // where the work is, which is not where either party lives
  items = [],
  adjustments = [],       // [{ label, amount }] — a discount is a negative one
  notes,
  externalUrl,             // wherever this document already lives
  documents = [],          // files attached to the document, not to a line
  terms,                   // the line under the total, if the document has one
  onBack,
  backLabel,               // "‹ Leads" rather than "‹ Estimates", when it came from a board
  onEdit,
  onSend,
  sendLabel = 'Send',
  extraActions = [],      // [{ key, label, icon, onClick, disabled }]
  // The whole toolbar, when the caller wants to decide it. Given as a list,
  // it REPLACES Send and Print rather than sitting beside them: a screen that
  // needs Edit, Send, Share, Reminders, PDF and Record Payment in that order
  // cannot get there by appending to two buttons it did not choose.
  actions,                // [{ key, label, icon, onClick, disabled, hidden }] | undefined
  // Estimate / Invoice, when a job has both. One document on screen at a
  // time, because a toolbar whose Send button sends one of two things the
  // reader cannot see is how the wrong document goes out.
  tabs,                   // { value, options: [{ key, label }], onChange } | undefined
  bannerSlot,             // anything the screen wants between the toolbar and the sheet
  settlement,             // { paid, balance } — an invoice that has been paid against
  share,                  // doc.share — sent / opened / answered, on his copy only
  busy,
}) {
  const label = kind === 'invoice' ? 'Invoice' : 'Estimate';
  const lines = items.map(i => {
    const qty = parseFloat(i.qty);
    const rate = parseFloat(i.unitCost);
    return {
      files: i.files || [],
      name: i.name || String(i.description || '').split('\n')[0] || 'Untitled line',
      description: i.name ? i.description : String(i.description || '').split('\n').slice(1).join('\n'),
      qty: isNaN(qty) ? 1 : qty,
      rate: isNaN(rate) ? 0 : rate,
      total: (isNaN(qty) ? 1 : qty) * (isNaN(rate) ? 0 : rate),
    };
  });
  const subtotal = lines.reduce((t, l) => t + l.total, 0);

  // Which columns this sheet draws. The Subtotal and the Total at the foot
  // are NOT affected: turning off the per-line figures is how Julian shows a
  // fixed price, and a document with no total on it anywhere is not a bill.
  const display = useDisplayOptions();
  const showRate = display.rate !== false;
  const showQty = display.qty !== false;
  const showTotals = display.itemTotals !== false;
  const cols = 1 + (showRate ? 1 : 0) + (showQty ? 1 : 0) + (showTotals ? 1 : 0);
  const adj = adjustments.filter(a => Number(a.amount));
  const total = adj.reduce((t, a) => t + Number(a.amount), subtotal);
  // A discount prints as −$120.00, not as $-120.00.
  const money = (n) => (n < 0 ? `\u2212${fmtMoney(Math.abs(n))}` : fmtMoney(n));

  return (
    <div className="pd">
      {/* On screen only. `print-hide` takes the whole toolbar off the paper. */}
      <div className="pd-bar print-hide">
        <button type="button" className="pd-bar-btn" onClick={onBack}>‹ {backLabel || `${label}s`}</button>
        <span className="pd-bar-title">{number || '—'}</span>
        {/* Edit lives in the toolbar when the screen builds its own. Two Edit
            buttons on one screen is two things to explain to a technician who
            has never used software before. */}
        {onEdit && !actions
          ? <button type="button" className="pd-bar-btn strong" onClick={onEdit}>Edit</button>
          : <span className="pd-bar-btn ghost" aria-hidden="true" />}
      </div>

      {tabs && tabs.options.length > 1 && (
        <div className="pd-tabs print-hide" role="tablist">
          {tabs.options.map(o => (
            <button
              key={o.key}
              type="button"
              role="tab"
              aria-selected={tabs.value === o.key}
              className={`pd-tab ${tabs.value === o.key ? 'on' : ''}`}
              onClick={() => tabs.onChange(o.key)}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      <div className="pd-acts print-hide">
        {actions ? actions.filter(a => a && !a.hidden).map(a => (
          <button key={a.key} type="button" className="pd-act" onClick={a.onClick} disabled={a.disabled || busy}>
            {a.icon}
            {a.label}
          </button>
        )) : <>
        {onSend && (
          <button type="button" className="pd-act" onClick={onSend} disabled={busy}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 3L10.5 13.5M21 3l-6.8 18-3.7-7.5L3 9.8 21 3z" /></svg>
            {busy ? 'Sending…' : sendLabel}
          </button>
        )}
        <button type="button" className="pd-act" onClick={() => window.print()}>
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M7 9V3h10v6M7 18H5a2 2 0 01-2-2v-5a2 2 0 012-2h14a2 2 0 012 2v5a2 2 0 01-2 2h-2M7 15h10v6H7v-6z" /></svg>
          Print
        </button>
        {/* Whatever this particular document can do next — raise an invoice
            off an approved estimate, post progress against an open invoice.
            The action belongs on the document it acts on. */}
        {extraActions.filter(Boolean).map(a => (
          <button key={a.key} type="button" className="pd-act" onClick={a.onClick} disabled={a.disabled || busy}>
            {a.icon}
            {a.label}
          </button>
        ))}
        </>}
      </div>

      {bannerSlot}

      <ShareBand share={share} />

      {/* "Part paid" is two words and one state, so the class is slugged —
          otherwise the band picks up a `.part` rule that does not exist and
          loses the `.paid` one that does. */}
      {status && (
        <div className={`pd-status print-hide ${String(status).toLowerCase().replace(/\s+/g, '-')}`}>
          {String(status).toUpperCase()}
        </div>
      )}

      <div className="pd-scroll">
        <div className="print-doc pd-sheet">
          <div className="pd-kind">{label.toUpperCase()}</div>

          <header className="pd-head">
            <div className="pd-from">
              {/* The business's own mark on its own paper. No logo on file just
                  means the name carries the letterhead, which is what every
                  document did before this existed. */}
              {from.logoUrl && <img className="pd-logo" src={from.logoUrl} alt="" />}
              <div className="pd-from-n">{from.name}</div>
              {(from.lines || []).filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
            </div>
            <Party label="Prepared For" party={to} />
          </header>

          {/* The job's address, labelled and on its own.
              It used to ride at the bottom of the Prepared For block, under
              QPS's phone and email, where it read as QPS's own address —
              which is the one thing it must never be mistaken for. This is
              the line that says which house the price is for. */}
          <div className="pd-mid">
            {serviceAddress ? (
              <div className="pd-service">
                <div className="pd-service-l">Service Address</div>
                <div className="pd-service-v">{serviceAddress}</div>
              </div>
            ) : <div />}
            <div className="pd-meta">
              <div><span>{label} #</span><b>{number || '—'}</b></div>
              <div><span>Date</span><b>{docDate(date)}</b></div>
            </div>
          </div>

          {/* ── WHICH COLUMNS SHOW ───────────────────────────────────────
              Julian's ask: a fixed price for "replace the gate" is a number
              the customer agreed to, and printing "5 × $514.95" under it
              invites an argument about the rate rather than about the doors.
              Other jobs are the opposite, where the quantities are exactly
              what make the price read as fair.

              So it is his choice, per document type, kept on the device —
              lib/displayOptions.js, the same switches PrintDoc already
              honoured. This sheet drew all four columns regardless, which is
              why turning them off appeared to do nothing.

              The column disappears entirely rather than printing blank: an
              empty Rate column on a customer's invoice reads like a fault. */}
          <table className="pd-table">
            <thead>
              <tr>
                <th>Description</th>
                {showRate && <th className="pd-r">Rate</th>}
                {showQty && <th className="pd-r">Quantity</th>}
                {showTotals && <th className="pd-r">Total</th>}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={cols} className="pd-none">No lines on this {label.toLowerCase()} yet.</td></tr>
              ) : lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <div className="pd-name">{l.name}</div>
                    <Scope description={l.description} />
                    {/* Attachments are named on the sheet rather than shown.
                        A reader needs to know an inspection report backs this
                        line; the report itself is opened in the app. */}
                    {l.files.length > 0 && (
                      <div className="pd-files">
                        {l.files.map((f, n) => <span key={n}>{f.name || 'Attachment'}</span>)}
                      </div>
                    )}
                  </td>
                  {showRate && <td className="pd-r pd-num">{fmtMoney(l.rate)}</td>}
                  {showQty && <td className="pd-r pd-num">{l.qty}</td>}
                  {showTotals && <td className="pd-r pd-num">{fmtMoney(l.total)}</td>}
                </tr>
              ))}
            </tbody>
          </table>

          {/* A technician's estimate passes no adjustments, so it ends at its
              own subtotal — no tax line, and no "amount due". What QPS bills a
              homeowner is QPS's business and never appears on a tech's sheet.
              QPS's own invoice passes its discount and its tax through
              `adjustments`, which is where those numbers are decided. */}
          <div className="pd-totals">
            <div className="pd-total-row"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
            {adj.map((a, i) => (
              <div key={i} className="pd-total-row"><span>{a.label}</span><span>{money(Number(a.amount))}</span></div>
            ))}
            <div className="pd-total-row grand"><span>Total</span><span>{fmtMoney(total)}</span></div>
            {/* What has actually been paid against it. Only on a document that
                has seen money — a brand-new invoice reading "Paid $0.00" is a
                line that tells you nothing and takes up the space of one that
                would. */}
            {settlement && Number(settlement.paid) > 0 && (
              <>
                <div className="pd-total-row"><span>Paid</span><span>{fmtMoney(settlement.paid)}</span></div>
                {/* More money came in than the bill asked for. "Balance
                    $-103.30" is a line nobody can act on; "Overpaid by
                    $103.30" is a refund somebody has to go and make. A
                    customer charged twice finds out from this line. */}
                {Number(settlement.balance) < -0.004 ? (
                  <div className="pd-total-row grand over">
                    <span>Overpaid by</span><span>{fmtMoney(Math.abs(settlement.balance))}</span>
                  </div>
                ) : (
                  <div className="pd-total-row grand"><span>Balance</span><span>{fmtMoney(settlement.balance)}</span></div>
                )}
              </>
            )}
          </div>

          {notes && (
            <section className="pd-notes">
              <div className="pd-notes-l">Notes</div>
              <div className="pd-notes-b">{notes}</div>
            </section>
          )}

          {/* The link and the attachments. Named on the sheet rather than
              embedded: a reader needs to know a supplier's bid backs this
              price, and opens it in the app. The link prints as its address,
              because paper has nothing to tap. */}
          {(href(externalUrl) || documents.length > 0) && (
            <section className="pd-notes">
              <div className="pd-notes-l">Attached</div>
              {href(externalUrl) && (
                <div className="pd-attach">
                  <a href={href(externalUrl)} target="_blank" rel="noopener noreferrer">{externalUrl}</a>
                </div>
              )}
              {documents.map((f, i) => (
                <div key={i} className="pd-attach">
                  <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name || 'Attachment'}</a>
                </div>
              ))}
            </section>
          )}

          {terms && <footer className="pd-foot">{terms}</footer>}
        </div>
      </div>
    </div>
  );
}
